import React, { createContext, useContext, useState } from "react";

// Reserved drawer id for the team sort/pick drawer (shared across all tabs).
export const TEAM_GROUP = "__team_sort__";

// Shared "Sort by Team" state.
//
// Team selection is now GLOBAL ("Team Surf"): the header's big PWHL button owns a
// persistent `surfTeam` that survives navigation, so a chosen team keeps filtering
// every tab as you surf. Per-page pickers were removed. A page can still set an
// explicit `sortTeam` (e.g. a /teams/:id page) which takes precedence; otherwise
// the global surf team is what every consumer reads.
const Ctx = createContext(null);

const readLS = (k, d) => { try { const v = localStorage.getItem(k); return v === null ? d : JSON.parse(v); } catch { return d; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };

export function SortByTeamProvider({ children }) {
    const [sortTeamLocal, setSortTeam] = useState(null); // page-explicit team (wins over surf)
    const [enabled, setEnabled] = useState(false);    // does the current page support team sort?
    const [teamIds, setTeamIds] = useState(null);     // optional season-aware eligible team ids
    const [extra, setExtra] = useState(null);         // optional control rendered before the All button
    const [allLabel, setAllLabel] = useState("All Teams");
    const [allowAll, setAllowAll] = useState(true);
    const [onTeamSelect, setOnTeamSelect] = useState(null);
    const [favTeam, setFavTeam] = useState(null);
    // Shared drawer state.
    const [openGroup, setOpenGroup] = useState(null); // id of the open drawer toggle (or TEAM_GROUP / null)
    const [defaultGroup, setDefaultGroup] = useState(null); // the tab's default open group (reverted to)
    const [ownTeamDrawer, setOwnTeamDrawer] = useState(false); // page renders its OWN team drawer (skip central)
    const [inlineCount, setInlineCount] = useState(0); // # of inline <TeamSortToggle/> mounted
    const registerInline = () => { setInlineCount((n) => n + 1); return () => setInlineCount((n) => Math.max(0, n - 1)); };

    // GLOBAL "Team Surf" selection — persisted so it survives route changes.
    const [surfTeam, setSurfTeamState] = useState(() => readLS("rosterly_surf_team", null));
    const [surfLocked, setSurfLockedState] = useState(() => readLS("rosterly_surf_locked", false));
    const setSurfTeam = (v) => { const nv = v || null; setSurfTeamState(nv); writeLS("rosterly_surf_team", nv); if (nv) { setPwhlSurfGroupState(null); writeLS("rosterly_pwhl_surf_group", null); setPwhlSurfMultiState([]); writeLS("rosterly_pwhl_surf_multi", []); } };
    const clearSurfMulti = () => { setPwhlSurfMultiState([]); writeLS("rosterly_pwhl_surf_multi", []); setNhlSurfMultiState([]); writeLS("rosterly_nhl_surf_multi", []); };
    // Ctrl+click multi-team selection (e.g. BOS + MTL). Cleared by any single/group pick.
    const [pwhlSurfMulti, setPwhlSurfMultiState] = useState(() => readLS("rosterly_pwhl_surf_multi", []) || []);
    const [nhlSurfMulti, setNhlSurfMultiState] = useState(() => readLS("rosterly_nhl_surf_multi", []) || []);
    // PWHL conference scope ("Eastern" / "Western"). Mutually exclusive with a single team.
    const [pwhlSurfGroup, setPwhlSurfGroupState] = useState(() => readLS("rosterly_pwhl_surf_group", null));
    const setPwhlSurfGroup = (v) => { const nv = v || null; setPwhlSurfGroupState(nv); writeLS("rosterly_pwhl_surf_group", nv); if (nv) { setPwhlSurfMultiState([]); writeLS("rosterly_pwhl_surf_multi", []); } };
    // SURF mode (off by default): ON = the team you click follows you to every page.
    const [surfMode, setSurfModeState] = useState(() => readLS("rosterly_surf_mode", false));
    // Turning Surf OFF also drops the surfed team/group/multi picks (both leagues).
    const setSurfMode = (v) => {
        const nv = !!v; setSurfModeState(nv); writeLS("rosterly_surf_mode", nv);
        if (!nv) {
            setSurfTeamState(null); writeLS("rosterly_surf_team", null);
            setNhlSurfTeamState(null); writeLS("rosterly_nhl_surf_team", null);
            setPwhlSurfGroupState(null); writeLS("rosterly_pwhl_surf_group", null);
            setNhlSurfGroupState(null); writeLS("rosterly_nhl_surf_group", null);
            setPwhlSurfMultiState([]); writeLS("rosterly_pwhl_surf_multi", []);
            setNhlSurfMultiState([]); writeLS("rosterly_nhl_surf_multi", []);
            setSortTeam(null);
        }
    };
    const setSurfLocked = (v) => { const nv = !!v; setSurfLockedState(nv); writeLS("rosterly_surf_locked", nv); };

    // Separate GLOBAL surf selection for the NHL league (abbr, e.g. "TOR").
    const [nhlSurfTeam, setNhlSurfTeamState] = useState(() => readLS("rosterly_nhl_surf_team", null));
    const setNhlSurfTeam = (v) => { const nv = v || null; setNhlSurfTeamState(nv); writeLS("rosterly_nhl_surf_team", nv); if (nv) { setNhlSurfMultiState([]); writeLS("rosterly_nhl_surf_multi", []); } };
    // NHL division/conference scope (e.g. "Atlantic" or "Eastern"). Mutually exclusive with a single team.
    const [nhlSurfGroup, setNhlSurfGroupState] = useState(() => readLS("rosterly_nhl_surf_group", null));
    const setNhlSurfGroup = (v) => { const nv = v || null; setNhlSurfGroupState(nv); writeLS("rosterly_nhl_surf_group", nv); if (nv) { setNhlSurfMultiState([]); writeLS("rosterly_nhl_surf_multi", []); } };
    // Ctrl+click: add/remove a team from the multi-team selection.
    const toggleSurfMulti = (lg, id) => {
        const nhl = lg === "nhl";
        const cur = nhl ? nhlSurfMulti : pwhlSurfMulti;
        const single = nhl ? nhlSurfTeam : surfTeam;
        const base = cur.length ? cur : (single ? [single] : []);
        const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
        if (nhl) { setNhlSurfGroupState(null); writeLS("rosterly_nhl_surf_group", null); } else { setPwhlSurfGroupState(null); writeLS("rosterly_pwhl_surf_group", null); }
        if (next.length <= 1) { (nhl ? setNhlSurfTeam : setSurfTeam)(next[0] || null); return; }
        if (nhl) { setNhlSurfTeamState(null); writeLS("rosterly_nhl_surf_team", null); setNhlSurfMultiState(next); writeLS("rosterly_nhl_surf_multi", next); }
        else { setSurfTeamState(null); writeLS("rosterly_surf_team", null); setPwhlSurfMultiState(next); writeLS("rosterly_pwhl_surf_multi", next); }
    };

    // Replace the whole multi-team selection (used by saved multi favourites/pins).
    const setSurfMulti = (lg, list) => {
        const ids = [...new Set(list || [])];
        if (ids.length <= 1) { (lg === "nhl" ? setNhlSurfTeam : setSurfTeam)(ids[0] || null); if (lg === "nhl") { setNhlSurfMultiState([]); writeLS("rosterly_nhl_surf_multi", []); } else { setPwhlSurfMultiState([]); writeLS("rosterly_pwhl_surf_multi", []); } return; }
        if (lg === "nhl") { setNhlSurfTeamState(null); writeLS("rosterly_nhl_surf_team", null); setNhlSurfGroupState(null); writeLS("rosterly_nhl_surf_group", null); setNhlSurfMultiState(ids); writeLS("rosterly_nhl_surf_multi", ids); }
        else { setSurfTeamState(null); writeLS("rosterly_surf_team", null); setPwhlSurfGroupState(null); writeLS("rosterly_pwhl_surf_group", null); setPwhlSurfMultiState(ids); writeLS("rosterly_pwhl_surf_multi", ids); }
    };

    // Effective sort team read by pages: a page's explicit pick wins, else the global surf.
    const sortTeam = sortTeamLocal || surfTeam;
    // ANY change of the surfed team (desktop board, mobile bar, arrow keys, pins) reaches the open page.
    const firstSurf = React.useRef(true);
    React.useEffect(() => {
        if (firstSurf.current) { firstSurf.current = false; return; }
        // Drop any page-local pick so the newly surfed team (or All) wins everywhere.
        setSortTeam(null);
    }, [surfTeam]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Ctx.Provider value={{
            sortTeam, setSortTeam, enabled, setEnabled, teamIds, setTeamIds,
            extra, setExtra, allLabel, setAllLabel, allowAll, setAllowAll,
            onTeamSelect, setOnTeamSelect, favTeam, setFavTeam,
            openGroup, setOpenGroup, defaultGroup, setDefaultGroup, ownTeamDrawer, setOwnTeamDrawer,
            inlineCount, registerInline,
            surfTeam, setSurfTeam, surfLocked, setSurfLocked, surfMode, setSurfMode,
            nhlSurfTeam, setNhlSurfTeam, nhlSurfGroup, setNhlSurfGroup,
            pwhlSurfGroup, setPwhlSurfGroup, pwhlSurfMulti, nhlSurfMulti, toggleSurfMulti, clearSurfMulti, setSurfMulti,
        }}>
            {children}
        </Ctx.Provider>
    );
}

export function useSortByTeam() {
    return useContext(Ctx) || {
        sortTeam: null, setSortTeam: () => {}, enabled: false, setEnabled: () => {},
        teamIds: null, setTeamIds: () => {}, extra: null, setExtra: () => {},
        allLabel: "All Teams", setAllLabel: () => {}, allowAll: true, setAllowAll: () => {},
        onTeamSelect: null, setOnTeamSelect: () => {}, favTeam: null, setFavTeam: () => {},
        openGroup: null, setOpenGroup: () => {}, defaultGroup: null, setDefaultGroup: () => {}, ownTeamDrawer: false, setOwnTeamDrawer: () => {},
        inlineCount: 0, registerInline: () => () => {},
        surfTeam: null, setSurfTeam: () => {}, surfLocked: false, setSurfLocked: () => {}, surfMode: false, setSurfMode: () => {},
        nhlSurfTeam: null, setNhlSurfTeam: () => {},
        nhlSurfGroup: null, setNhlSurfGroup: () => {},
        pwhlSurfGroup: null, setPwhlSurfGroup: () => {}, pwhlSurfMulti: [], nhlSurfMulti: [], toggleSurfMulti: () => {}, clearSurfMulti: () => {}, setSurfMulti: () => {},
    };
}
